import React from "react";
import Select, { type StylesConfig } from "react-select";
import {
  REACT_SELECT_PORTAL_PROPS,
  getReactSelectMenuPlacement,
  reactSelectPortalStyles,
} from "./reactSelectUtils";

export type PortalSelectOption = {
  label: string;
  value: number | string;
};

type PortalSelectProps = {
  options: PortalSelectOption[];
  value: PortalSelectOption | null;
  onChange: (value: PortalSelectOption | null) => void;
  placeholder?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  isSearchable?: boolean;
  obstacleSelectors?: string[];
  preferredMenuHeight?: number;
  className?: string;
};

const getPortalSelectStyles = (
  isInvalid?: boolean
): StylesConfig<PortalSelectOption, false> => ({
  ...(reactSelectPortalStyles as StylesConfig<PortalSelectOption, false>),
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    borderColor: isInvalid
      ? "#dc3545"
      : state.isFocused
        ? "#86b7fe"
        : "#ced4da",
    boxShadow: isInvalid
      ? "0 0 0 0.25rem rgba(220, 53, 69, 0.25)"
      : state.isFocused
        ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
        : "none",
    "&:hover": {
      borderColor: isInvalid ? "#dc3545" : "#ced4da",
    },
  }),
});

const PortalSelect: React.FC<PortalSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select",
  isDisabled,
  isInvalid,
  isSearchable = true,
  obstacleSelectors,
  preferredMenuHeight = 220,
  className = "",
}) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [menuPlacement, setMenuPlacement] = React.useState<"top" | "bottom">("bottom");
  const [maxMenuHeight, setMaxMenuHeight] = React.useState(preferredMenuHeight);

  const handleMenuOpen = () => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect) return;

    const placement = getReactSelectMenuPlacement(rect, {
      preferredMenuHeight,
      obstacleSelectors,
    });
    setMenuPlacement(placement.placement);
    setMaxMenuHeight(placement.maxMenuHeight);
  };

  return (
    <div ref={wrapperRef} className={className}>
      <Select
        {...REACT_SELECT_PORTAL_PROPS}
        classNamePrefix="react-select"
        options={options}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isSearchable={isSearchable}
        menuPlacement={menuPlacement}
        maxMenuHeight={maxMenuHeight}
        onMenuOpen={handleMenuOpen}
        styles={getPortalSelectStyles(isInvalid)}
      />
    </div>
  );
};

export default PortalSelect;
